import { useQuery } from '@tanstack/react-query';

import { CreateSurchargeDTO, SurchargeDTO } from '@invenco/shipping-interface';

import { useGateways } from '@/gateways/GatewayProvider';
import { useGatewayMutation } from '@/shared/hooks/queries';

/**
 * Loads the surcharges for a carrier (carrier-scoped plus rows scoped to its services, including
 * future-dated and superseded versions) and the mutations to add or delete a row. Rows are
 * immutable on the backend — "updating a rate" is a create with the same name/scope and a later
 * validFrom — so there is no update mutation. UI-specific derivations (service names, per-row
 * status) live in the components that need them.
 */
export function useCarrierSurcharges(carrierId: string | undefined) {
  const { shippingGateway } = useGateways();
  const surchargesQueryKey = ['carriers', carrierId, 'surcharges'];

  const {
    data: surcharges,
    isLoading: isLoadingSurcharges,
    isError: hasSurchargesError,
    refetch: refetchSurcharges,
  } = useQuery({
    queryKey: surchargesQueryKey,
    queryFn: ({ signal }) => shippingGateway.getSurcharges({ carrierId: carrierId! }, { signal }),
    enabled: !!carrierId,
  });

  const { mutate: createSurcharge } = useGatewayMutation<SurchargeDTO, CreateSurchargeDTO>({
    mutationFn: (input) => shippingGateway.createSurcharge(input),
    linkedQuery: surchargesQueryKey,
    linkResponseToQuery: false,
    successMessage: 'Surcharge saved',
  });

  const { mutate: deleteSurcharge } = useGatewayMutation<void, string>({
    mutationFn: (surchargeId) => shippingGateway.deleteSurcharge(surchargeId),
    linkedQuery: surchargesQueryKey,
    linkResponseToQuery: false,
    successMessage: 'Surcharge deleted',
  });

  return {
    surcharges,
    isLoadingSurcharges,
    hasSurchargesError,
    refetchSurcharges,
    createSurcharge,
    deleteSurcharge,
  };
}

export type CarrierSurchargesData = ReturnType<typeof useCarrierSurcharges>;
